import type { Article, ArticlePreview } from 'interfaces/content/articles/Articles';
import useDateTime from 'hooks/useDateTime';
import * as S from 'components/SidebarContentPreview/SidebarContentPreviewItem/SidebarContentPreviewItemStyles';
import Link from 'components/Globals/Base/Link';
import BylineList from 'components/Byline';
import { NextCustomImage } from 'components/Image';
import { ellipsis } from 'utils/stringUtils';
import { setEventAction } from 'utils/trackDataUtils';
import { parseCaptionWhenAltTextEmpty } from 'utils/parsingUtils/clientSideParsing';
import { COMMON_TEST_IDS } from 'constants/testsIds/common';

interface SidebarContentPreviewArticleProps {
  entry: ArticlePreview;
  contentPlacementCD: string;
  parent?: Article;
  title?: string;
}

const SidebarContentPreviewArticle = ({
  entry,
  contentPlacementCD,
  parent,
  title,
}: SidebarContentPreviewArticleProps) => {
  const localDate = useDateTime(entry.date, 'date');
  const recommendsArticle = entry.titleLink.match(/recommends/g);
  const trackingData = {
    articleCategoryCD: parent?.primarySection.name,
    articleNameCD: parent?.title,
    authorNameCD: parent?.authorNames,
    cmsCategoryCD: parent?.pageType,
    cmsSubCategoryCD: parent?.primarySection.name,
    contentPlacementCD,
    contentPublishedDateCD: parent?.dateGmt,
    contentTagsCD: parent?.tagNames,
    eventAction: setEventAction(entry.eyebrowSectionLabel, entry.eyebrowTagLabel),
    eventCategory: 'navigation',
    eventLabel: `title click: ${entry.title}`,
    premiumCategoryCD: parent?.premiumCategory.name,
  };

  const href = title === 'Most Popular' ? `${entry.eyebrowSectionLink}/` : entry.eyebrowSectionLink;

  return (
    <S.Card data-cy={COMMON_TEST_IDS.SIDEBAR_WRAPPER}>
      <Link
        dataCy={COMMON_TEST_IDS.SIDEBAR_SECTION}
        href={href}
        className='section'
        trackingData={{ ...trackingData, eventLabel: `category click: ${entry.title}` }}
        type='red'
      >
        {entry.eyebrowSectionLabel}
      </Link>
      <S.Content>
        <S.Left>
          <Link
            ariaLabel={entry.title}
            href={entry.titleLink}
            className='title-link'
            trackingData={trackingData}
            type='primary'
            data-cy={COMMON_TEST_IDS.SIDEBAR_DESCRIPTION}
          >
            {ellipsis(entry.title, 120)}
          </Link>
        </S.Left>
        <S.Right>
          <Link
            ariaLabel={entry.image.altText === '' ? 'image' : entry.image.altText}
            href={entry.titleLink}
            className='image-link'
            type='image'
            dataCy={COMMON_TEST_IDS.SIDEBAR_IMAGE_LINK}
          >
            <NextCustomImage
              src={entry.image.mediaItemUrl}
              data-cy={COMMON_TEST_IDS.SIDEBAR_CARD_IMAGE}
              alt={parseCaptionWhenAltTextEmpty(entry.image)}
              width={115}
              height={76.07}
            />
          </Link>
        </S.Right>
      </S.Content>
      {!recommendsArticle && (
        <S.PublishedDate data-cy={COMMON_TEST_IDS.SIDEBAR_ARTICLE_PUBLISH_DATE}>{localDate}</S.PublishedDate>
      )}
      <BylineList
        dataCy={COMMON_TEST_IDS.SIDEBAR_ARTICLE_AUTHOR}
        title={title}
        bylineList={entry.authors}
      />
    </S.Card>
  );
};

export default SidebarContentPreviewArticle;
